import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system';
import { Clipboard, ClipboardCheck, Info } from 'lucide-react';

export function ClientLinkAdvisorBlock({ advisorId }: { advisorId?: string }) {
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const copyLink = async () => {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard is unavailable');
      await navigator.clipboard.writeText(window.location.href);
      setCopyFailed(false);
      setLinkCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
      setCopyFailed(true);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <Info className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="mt-5 text-2xl font-semibold text-gray-900">This Link is for Clients</h1>
          <p className="mt-3 text-gray-700">
            This link is for clients to complete their intake form. Since you're logged in as an
            advisor, you can't open it as a client.
          </p>
        </div>

        <div className="mt-8 space-y-3">
          <Button
            className="w-full rounded-full py-6 text-base font-medium"
            onClick={copyLink}
          >
            {linkCopied ? (
              <ClipboardCheck className="mr-2 h-5 w-5" />
            ) : (
              <Clipboard className="mr-2 h-5 w-5" />
            )}
            {linkCopied ? 'Link copied' : 'Copy link'}
          </Button>
          {copyFailed && (
            <p
              className="text-center text-sm text-red-700"
              role="alert"
            >
              Unable to copy link. Please copy it from your browser.
            </p>
          )}
          <Button
            variant="outline"
            className="w-full rounded-full py-6 text-base font-medium"
            onClick={() => navigate(advisorId ? `/advisors/${advisorId}/clients` : '/advisors')}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
